import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Toast + Undo — §5. The archive action's reversal lives here.
 *
 * Built on **`react-aria`'s `useToastRegion` / `useToast`** with `react-stately`'s
 * `useToastState`, rather than RAC's `UNSTABLE_Toast*`. Two reasons, and the second is the
 * one that decided it: an `UNSTABLE_` symbol has no business in the public surface of a
 * package every Studio view imports; and RAC's toast wants a **module-level queue
 * singleton**, which is global mutable state outside React — it leaks between tests and it
 * cannot belong to a provider. `useToastState` is a hook, so the queue stays exactly where
 * the app already keeps it.
 *
 * What that buys over the hand-rolled version: the region is a landmark reachable with F6,
 * the toast is `role="alertdialog"` so it is announced without stealing focus, hovering or
 * focusing it **pauses the timer**, and focus returns to where it was when the last one goes.
 */
import { useRef } from 'react';
import { useToast as useAriaToast, useToastRegion } from 'react-aria';
import { useToastState } from 'react-stately';
/** One at a time: a second message replaces the first rather than stacking. */
export function useStudioToastState() {
    return useToastState({ maxVisibleToasts: 1 });
}
export function ToastRegion({ state, label }) {
    const ref = useRef(null);
    const { regionProps } = useToastRegion({ 'aria-label': label }, state, ref);
    if (state.visibleToasts.length === 0)
        return null;
    return (_jsx("div", { ...regionProps, ref: ref, className: "toast-live on", children: state.visibleToasts.map((toast) => (_jsx(ToastItem, { toast: toast, state: state }, toast.key))) }));
}
function ToastItem({ toast, state, }) {
    const ref = useRef(null);
    const { toastProps, contentProps, titleProps } = useAriaToast({ toast }, state, ref);
    const { message, undoLabel, onUndo } = toast.content;
    return (_jsxs("div", { ...toastProps, ref: ref, className: "toast", children: [_jsx("div", { ...contentProps, className: "t-body", children: _jsx("span", { ...titleProps, className: "t-msg", children: message }) }), onUndo !== undefined && (_jsx("button", { type: "button", className: "link", onClick: () => {
                    state.close(toast.key);
                    onUndo();
                }, children: undoLabel }))] }));
}
/**
 * The toast's shape on its own — what the kit puts on the spec sheet.
 *
 * Static and inert by design: a live toast is a queued one, and a page that draws the
 * component to show what it looks like should not also open a landmark and start a timer.
 */
export function Toast({ children, undoLabel, onUndo }) {
    return (_jsxs("div", { className: "toast", children: [_jsx("div", { className: "t-body", children: _jsx("span", { className: "t-msg", children: children }) }), onUndo !== undefined && (_jsx("button", { type: "button", className: "link", onClick: onUndo, children: undoLabel }))] }));
}
//# sourceMappingURL=Toast.js.map